import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { getArticleNote, saveArticleNote, deleteArticleNote } from '../repositories/noteRepository';
import { IconEditSquare } from './icons';

interface ArticleNotePanelProps {
  articleId: string;
  onHasContent?: (has: boolean) => void;
  onDeleted?: () => void;
  hidden?: boolean;
}

export interface ArticleNotePanelHandle {
  focusAtEnd: () => void;
  saveNow: () => void;
}

const AUTOSAVE_MS = 2000;

export const ArticleNotePanel = forwardRef<ArticleNotePanelHandle, ArticleNotePanelProps>(
  function ArticleNotePanel({ articleId, onHasContent, onDeleted, hidden }, ref) {
    const [note, setNote] = useState('');
    const [savedNote, setSavedNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const articleIdRef = useRef(articleId);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => ({
      focusAtEnd() {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
      },
      saveNow() {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        persist(note, articleId);
      },
    }));

    useEffect(() => {
      articleIdRef.current = articleId;
      setLoaded(false);
      setNote('');
      setSavedNote('');
    }, [articleId]);

    useEffect(() => {
      if (loaded) return;
      let cancelled = false;
      getArticleNote(articleId).then((data) => {
        if (cancelled) return;
        const text = data.note ?? '';
        setNote(text);
        setSavedNote(text);
        setLoaded(true);
        onHasContent?.(text.trim().length > 0);
      }).catch(() => {
        if (cancelled) return;
        setLoaded(true);
      });
      return () => { cancelled = true; };
    }, [articleId, loaded, onHasContent]);

    const persist = useCallback((text: string, id: string) => {
      setSaving(true);
      saveArticleNote(id, text)
        .then((data) => {
          if (id !== articleIdRef.current) return;
          const saved = data.note ?? text;
          setSavedNote(saved);
          onHasContent?.(saved.trim().length > 0);
        })
        .catch(() => {/* silent – next autosave will retry */})
        .finally(() => {
          if (id === articleIdRef.current) setSaving(false);
        });
    }, [onHasContent]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const val = e.target.value;
      setNote(val);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        persist(val, articleId);
      }, AUTOSAVE_MS);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        (e.target as HTMLTextAreaElement).blur();
        return;
      }
      if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        persist(note, articleId);
      }
    };

    const handleDelete = () => {
      if (!window.confirm('Delete this note?')) return;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      deleteArticleNote(articleId)
        .then(() => {
          setNote('');
          setSavedNote('');
          onHasContent?.(false);
          onDeleted?.();
        })
        .catch(() => {/* silent */});
    };

    useEffect(() => {
      return () => {
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }, [articleId]);

    const isDirty = note !== savedNote;

    if (!loaded) {
      return hidden ? null : <div className="article-note-panel"><div className="article-note-loading">Loading…</div></div>;
    }

    return (
      <div className="article-note-panel" style={hidden ? { display: 'none' } : undefined}>
        <textarea
          ref={textareaRef}
          className="article-note-textarea"
          placeholder="Add a note (markdown)…"
          value={note}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          rows={5}
        />
        <div className="article-note-footer">
          <div className="article-note-status">
            {saving ? 'Saving…' : isDirty ? 'Unsaved changes' : note.trim() ? 'Saved' : ''}
          </div>
          {savedNote.trim() && (
            <button
              className="article-note-delete-btn"
              onClick={handleDelete}
              title="Delete note"
            >
              Delete note
            </button>
          )}
        </div>
      </div>
    );
  }
);

export function IconNote({ hasContent }: { hasContent: boolean }) {
  return <IconEditSquare filled={hasContent} />;
}
